import { TClusterUserModalViewMode } from './../../shared/modals/user-management/cluster-user/cluster-user-modals.config';
import { HttpResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Promise } from 'q';
import { Observable, of } from 'rxjs';
import 'rxjs/add/operator/switchMap';
import { catchError, mergeMap, switchMap, takeLast, tap, withLatestFrom } from 'rxjs/operators';
import { BackgroundJob } from 'Src/ng2/shared/services/background-job/background-job.service';
import { ISnackBarData, SnackBarService } from 'Src/ng2/shared/services/snackbar/snackbar.service';
import { UtilitiesService } from 'Src/ng2/shared/services/utilities/utilities.service';
import { IStudentMini } from 'Src/ng2/shared/typings/interfaces/student.interface';
import {
  IStudentSupport,
  TValidStudentSupportBackendStatuses,
} from '../../shared/typings/interfaces/student-support.interface';
import { LoadDocLogs } from '../actions/doc-logs-actions';
import { LoadFlags } from '../actions/flag-actions';
import * as studentSupportsActions from '../actions/student-supports-actions';
import { LoadStudentSupports } from '../actions/student-supports-actions';
import { ApiService } from './../../shared/services/api-service/api-service';
import { BackgroundJobNotificationService } from './../../shared/services/background-job-notification-service/background-job-notification-service';
import { IUpdatePayload } from './../actions/flag-actions';
interface IAction {
  type: string;
  payload: {
    schoolId: string;
  };
}

interface IUpdateAction {
  type: string;
  payload: IUpdatePayload<IStudentSupport>;
}

export interface IStudentSupportUpdatePayload {
  studentSupportIds: string[];
  status: TValidStudentSupportBackendStatuses;
}

@Injectable()
export class StudentSupportsEffects {
  batchSuccessMessage: string;
  batchFailedMessage: string;
  snackBarData: ISnackBarData = { message: '', duration: 3000 };

  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private injector: Injector,
    private backgroundJob: BackgroundJob,
    private snackBarService: SnackBarService,
    private store: Store<any>,
    private utilitiesService: UtilitiesService,
    private backgroundJobNotificationService: BackgroundJobNotificationService,
  ) {
    this.batchSuccessMessage = 'Batch action applied to the selected students.';
    this.batchFailedMessage =
      'The batch action could not be applied to all of the selected students. Please try again.';
  }

  private getJobPromise(response: HttpResponse<any>, msg: string): Promise<any> {
    const jobId = response.headers.get('nv-background-jobs');
    const backgroundJobSpinner = this.injector.get('BackgroundJobSpinner');
    const promise = (this.backgroundJob as any)(jobId).promise();
    const job = null;
    backgroundJobSpinner.activateSpinner(msg, promise, job);
    return promise;
  }

  private sendSnack(success: true | false): void {
    this.snackBarService.showBatchActionToast({ success: success ? true : false });
  }

  private sendSdcAction(action, payload): void {
    const sdcActions = this.injector.get('sdcActions');
    // if the slice of studentSupports on SDCStore is not initialized yet, don't send further action (Jamson, Julia)
    const sdcStudentSupports = sdcActions.SDCStore.getAllStudentSupports();
    const sdcStudentSupportsLoaded = sdcStudentSupports.length;
    if (!sdcStudentSupportsLoaded) {
      return;
    }
    // Note: `sendSdcAction` is being called inconsistently
    // sometimes it is called with the API payload and sometimes with just the array of supports
    // the check below is to guard against both scenarios (CM)
    const _payload = payload.data || payload;
    switch (action) {
      case 'create':
        sdcActions.addStudentSupport(_payload);
        break;
      case 'update':
        sdcActions.updateStudentSupports(_payload);
        break;
      case 'setAllStudentSupports':
        sdcActions.setAllStudentSupports(_payload);
        break;
      default:
        return null;
    }
  }

  @Effect()
  loadStudentSupports$ = this.actions$.pipe(
    ofType(studentSupportsActions.LOAD_STUDENT_SUPPORTS),
    switchMap((action: IAction) => {
      const { schoolId } = action.payload;
      return this.apiService.getStudentSupports(schoolId).pipe(
        tap((ss: IStudentSupport[]) => {
          const sdcStorePayload = this.utilitiesService.toPOJO(ss);
          this.sendSdcAction('setAllStudentSupports', sdcStorePayload);
        }),
        mergeMap((res: any) => {
          return [new studentSupportsActions.LoadStudentSupportsSuccess(res.data)];
        }),
        catchError(error => {
          return of(new studentSupportsActions.LoadStudentSupportsFail(error));
        }),
      );
    }),
  );

  // additional refactoring to dry up repeated switchMap code across each effect is being handled in
  // https://www.pivotaltracker.com/story/show/160791378 - JYR
  @Effect()
  createStudentSupport$ = this.actions$.pipe(
    ofType(studentSupportsActions.CREATE_STUDENT_SUPPORT),
    switchMap((action: any) => {
      // TODO: add ICreateAction?
      const { support, studentId, schoolId } = action.payload;
      const studentSupportSvc = this.injector.get('StudentSupport');
      const studentSupportFromTemplate = studentSupportSvc.createTemplateFromSupport(support);
      studentSupportFromTemplate.student = { studentId };
      studentSupportFromTemplate._id = this.utilitiesService.createV4Uuid();
      const studentSupportToCreate = new studentSupportSvc(studentSupportFromTemplate);
      return this.apiService.createStudentSupport(studentSupportToCreate, schoolId).pipe(
        switchMap((studentSupport: IStudentSupport) => {
          return [new studentSupportsActions.CreateStudentSupportSuccess(studentSupport)];
        }),
        catchError(error => {
          return of(new studentSupportsActions.CreateStudentSupportFailure(error));
        }),
      );
    }),
  );

  @Effect()
  updateStudentSupports$ = this.actions$.pipe(
    ofType(studentSupportsActions.UPDATE_STUDENT_SUPPORT),
    switchMap((action: IUpdateAction) => {
      const { id, patch } = action.payload;
      return this.apiService.patchStudentSupport(id, patch).pipe(
        switchMap((studentSupport: IStudentSupport) => {
          return [new studentSupportsActions.UpdateStudentSupportSuccess(studentSupport)];
        }),
        catchError(error => {
          return of(new studentSupportsActions.UpdateStudentSupportFail(error));
        }),
      );
    }),
  );

  @Effect({ dispatch: false })
  bulkCreateStudentSupports$ = this.actions$.pipe(
    ofType(studentSupportsActions.BULK_CREATE_STUDENT_SUPPORTS),
    switchMap((action: any) => {
      const { studentIds, support } = action.payload;
      const studentSupportSvc = this.injector.get('StudentSupport');
      const studentSupportIds: string[] = studentSupportSvc.createStudentSupportIdsFromStudentIds(studentIds);
      const studentSupport: IStudentSupport = studentSupportSvc.createTemplateFromSupport(support);
      const apiPayload = { studentSupportIds, studentSupport };
      const studentMini: IStudentMini = { studentId: null, lastFirst: null };
      studentSupport[`student`] = studentMini;
      const sdcStorePayload = {
        studentIdsToStudentSupportIds: studentSupportIds,
        studentSupport,
      };

      const { schoolId } = studentSupport;
      const httpResponseObsv$: Observable<HttpResponse<object>> = this.apiService.createStudentSupports(apiPayload);
      return httpResponseObsv$.pipe(
        takeLast(1),
        switchMap(response => {
          return of(this.getJobPromise(response, 'Assigning student supports....'));
        }),
        tap(promise => {
          promise.then(() => {
            this.store.dispatch(new LoadStudentSupports({ schoolId }));
            this.store.dispatch(new LoadFlags({ schoolId }));
            this.store.dispatch(new LoadDocLogs({ studentId: studentIds, schoolId }));
            this.sendSdcAction('create', sdcStorePayload);
            this.sendSnack(true);
            this.backgroundJobNotificationService.sendMessage({ backgroundJob: 'BulkStudentSupportCreate' });
          });
          return promise;
        }),
        catchError(error => {
          this.sendSnack(false);
          return of(new studentSupportsActions.BulkCreateStudentSupportsFail(error));
        }),
      );
    }),
  );

  @Effect()
  bulkUpdateStudentSupports$ = this.actions$.pipe(
    ofType<any>(studentSupportsActions.BULK_UPDATE_STUDENT_SUPPORTS),
    withLatestFrom(this.store),
    switchMap(([action]) => {
      const payload: IStudentSupportUpdatePayload = action.payload;
      const { studentSupportIds, status } = payload;
      const sdcStorePayload = { studentSupportIds, patch: { status } };
      const httpResponseObsv$: Observable<HttpResponse<object>> = this.apiService.patchStudentSupports(payload);
      return httpResponseObsv$.pipe(
        takeLast(1),
        switchMap(response => {
          return of(this.getJobPromise(response, 'Updating student supports...'));
        }),
        tap(promise => {
          promise.then(() => {
            this.sendSdcAction('update', sdcStorePayload);
            this.sendSnack(true);
            this.backgroundJobNotificationService.sendMessage({ backgroundJob: 'BulkStudentSupportUpdate' });
          });
          return promise;
        }),
        mergeMap(() => {
          return [new studentSupportsActions.BulkUpdateStudentSupportsSuccess(payload)];
        }),
        catchError(error => {
          this.sendSnack(false);
          return of(new studentSupportsActions.BulkUpdateStudentSupportsFail(error));
        }),
      );
    }),
  );
}
